import { Grid, TextField, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { useMemo } from "react";
import { enqueueSnackbar } from 'notistack';

export const TaxField = ({ label, value, disabled = false, onValueChange, originalValue }: TaxFieldProps) => {
    
    const handleFieldChange = (field: keyof TTaxValue) => (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        if (newValue === '' || /^\d*(,\d{0,2})?$/.test(newValue)) {
            if (originalValue && newValue) {
                if (field === 'valor') {
                    const valorOriginal = parseFloat(originalValue.valor.replace(',', '.')) || 0;
                    const novoValor = parseFloat(newValue.replace(',', '.')) || 0;
                    
                    if (novoValor > valorOriginal) {
                        const valorOriginalFormatado = originalValue.valor.replace('.', ',') ?? '0,00';
                        enqueueSnackbar(
                            `${label}: Não é permitido aumentar o valor do imposto. Valor original do XML: R$ ${valorOriginalFormatado}`,
                            { variant: "error" }
                        );
                        return;
                    }
                }
                
                if (field === 'base_calculo') {
                    const baseOriginal = parseFloat(originalValue.base_calculo.replace(',', '.')) || 0;
                    const novaBase = parseFloat(newValue.replace(',', '.')) || 0;
                    
                    if (novaBase > baseOriginal) {
                        const baseOriginalFormatada = originalValue.base_calculo.replace('.', ',') ?? '0,00';
                        enqueueSnackbar(
                            `${label}: Não é permitido aumentar a base de cálculo. Base original do XML: R$ ${baseOriginalFormatada}`,
                            { variant: "error" }
                        );
                        return;
                    }
                }
            }
            
            onValueChange({
                ...value,
                [field]: newValue
            });
        }
    };

    // Validação: valor = base_calculo * (aliquota / 100)
    const validationError = useMemo(() => {
        const baseCalculo = parseFloat(value.base_calculo.replace(',', '.')) || 0;
        const aliquota = parseFloat(value.aliquota.replace(',', '.')) || 0;
        const valorInformado = parseFloat(value.valor.replace(',', '.')) || 0;


        if (!value.base_calculo && !value.aliquota && !value.valor) {
            return null;
        }

        const camposPreenchidos = [value.base_calculo, value.aliquota, value.valor].filter(v => v).length;
        if (camposPreenchidos >= 2) {
            const valorCalculado = (baseCalculo * aliquota) / 100;
            const diferenca = Math.abs(valorCalculado - valorInformado);
            
            if (diferenca > 0.01) {
                return true;
            }
        }

        return null;
    }, [value.base_calculo, value.aliquota, value.valor]);

    return (
        <Grid container size={{ xs: 12 }}>
            <Grid size={{ xs: 4 }}>
                <Typography variant="body2" sx={{ flex: 1, minWidth: 150 }}>
                    {label}
                </Typography>
            </Grid>
            <Grid size={{ xs: 8 }}>
                <Box sx={{ display: 'flex', alignItems: 'space-around', gap: 2, mb: 1 }}>
                    <TextField
                        size="small"
                        value={value.base_calculo.replace('.', ',') ?? ''}
                        onChange={handleFieldChange('base_calculo')}
                        sx={{ width: 150 }}
                        disabled={disabled}
                        placeholder="0,00"
                        slotProps={{
                            htmlInput: {
                                inputMode: 'decimal',
                            }
                        }}
                    />

                    <TextField
                        size="small"
                        value={value.aliquota.replace('.', ',') ?? ''}
                        onChange={handleFieldChange('aliquota')}
                        sx={{ width: 150 }}
                        disabled={disabled}
                        placeholder="0,00"
                        slotProps={{
                            htmlInput: {
                                inputMode: 'decimal',
                            }
                        }}
                    />

                    <TextField
                        size="small"
                        value={value.valor.replace('.', ',') ?? ''}
                        onChange={handleFieldChange('valor')}
                        sx={{ width: 150 }}
                        disabled={disabled}
                        placeholder="0,00"
                        error={!!validationError}
                        slotProps={{
                            htmlInput: {
                                inputMode: 'decimal',
                            }
                        }}
                    />


                </Box>
            </Grid>
        </Grid>
    );
};

interface TaxFieldProps {
    label: string;
    value: TTaxValue;
    disabled?: boolean;
    onValueChange: (value: TTaxValue) => void;
    originalValue?: TTaxValue;
}

type TTaxValue = {
    base_calculo: string;
    aliquota: string;
    valor: string;
}